import type { FC } from 'react';

import PhoneMockup from '@/features/home/components/PhoneMockup';
import { SECTION_IDS } from '@/features/home/constants/home.constants';
import { SITE_CONTAINER } from '@/features/home/constants/homeStyles.constants';
import type { HeroCopy, HeroHeadline, PhoneScreen, StoreBadge } from '@/types/home.types';

interface HeroSectionProps {
  eyebrow: string;
  headline: HeroHeadline;
  copy: HeroCopy;
  screens: PhoneScreen[];
  activeScreenId: PhoneScreen['id'];
  onSelectScreen: (id: PhoneScreen['id']) => void;
  badges: StoreBadge[];
}

const HeroSection: FC<HeroSectionProps> = ({
  eyebrow,
  headline,
  copy,
  screens,
  activeScreenId,
  onSelectScreen,
  badges,
}) => (
  <section id={SECTION_IDS.top} className="bg-site-bg">
    <div
      className={`${SITE_CONTAINER} grid grid-cols-[repeat(auto-fit,minmax(min(100%,440px),1fr))] items-center gap-14 pt-14 pb-16 md:pt-[72px] md:pb-20`}
    >
      <div>
        <span className="block font-site text-[13px] tracking-[0.08em] text-site-accent-deep uppercase">
          {eyebrow}
        </span>

        <h1 className="mt-5 -ml-[0.05em] font-site text-[clamp(40px,5vw,68px)] leading-[1.02] font-extrabold tracking-[-0.025em] text-balance">
          <span className="block">{headline.lead}</span>
          <span className="block text-site-accent">{headline.accent}</span>
        </h1>

        <div className="mt-7 flex max-w-[46ch] flex-col gap-4 font-site">
          <p className="font-site text-lg leading-[1.55]">{copy.problem}</p>

          <p className="font-site text-[24px] font-extrabold tracking-[-0.01em]">
            {copy.builtPrefix}
            <span className="text-site-accent">{copy.builtBrand}</span>.
          </p>

          <p className="border-l-2 border-site-ink pl-4 font-site text-lg leading-[1.55]">
            {copy.pullQuote}
          </p>

          <p className="font-site text-lg leading-[1.55]">{copy.outcome}</p>

          <p className="font-site text-[15px] font-extrabold tracking-[0.08em] text-site-accent-deep uppercase">
            {copy.kicker}
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-4">
          {badges.map((badge) => (
            <a
              key={badge.platform}
              href={badge.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3.5 border-2 border-site-ink bg-white py-3 pr-[18px] pl-3 transition-colors hover:border-site-accent"
            >
              <img
                src={badge.qrSrc}
                alt={badge.qrAlt}
                width={88}
                height={88}
                className="block h-[88px] w-[88px]"
              />
              <span className="flex flex-col gap-1">
                <span className="font-site text-[12px] tracking-[0.08em] text-site-ink/70 uppercase">
                  {badge.scanLabel}
                </span>
                <span className="font-site text-[20px] font-extrabold">{badge.storeName}</span>
              </span>
            </a>
          ))}
        </div>
      </div>

      <PhoneMockup
        screens={screens}
        activeScreenId={activeScreenId}
        onSelectScreen={onSelectScreen}
      />
    </div>
  </section>
);

export default HeroSection;
